import React, {
    ReactNode,
    useEffect,
    useState,
} from "react";

import {
    RiArrowLeftSLine,
    RiArrowRightSLine,
} from "@remixicon/react";
import classNames from "classnames";
import {
    Dropdown,
} from "react-bootstrap";
// eslint-disable-next-line import/no-extraneous-dependencies
import { DayPickerSingleDateController } from "react-dates";
// eslint-disable-next-line import/no-extraneous-dependencies
import "react-dates/initialize";

// eslint-disable-next-line import/no-extraneous-dependencies
import moment from "moment";
import { createPortal } from "react-dom";
import Styles from "./DateRangeCalendarPicker.module.scss";

// eslint-disable-next-line import/no-extraneous-dependencies
import "react-dates/lib/css/_datepicker.css";
import { Time, toInputHourMinute, tryParseDate } from "../../../date";
import Button from "../../theme/button/Button";
import TimePicker, { Time as TimeDate } from "../time/TimePicker";

export interface DateCalendarPickerProps {
    value: Date | string | undefined | null;
    className?: string;
    calendarClassName?: string;
    onChange: (value: Date | undefined) => void;
    icon?: ReactNode;
    toggleText?: string;
    allowTime?: boolean;
    allowNewTime?: boolean;
    shouldMenu?: boolean;
    shouldPortal?: boolean;
    shouldShowDone?: boolean;
}

export default function DateCalendarPicker(props: DateCalendarPickerProps): ReactNode {
    const { value } = props;
    const [date, setMomentDate] = useState<moment.Moment | null>(value ? moment(value) : null);

    const [focusedInput, setFocusedInput] = useState<boolean>(true);

    useEffect(() => {
        const parsed = tryParseDate(value);
        if (date?.valueOf() !== parsed?.getTime()) {
            setMomentDate(value ? moment(value) : null);
        }
    }, [value]);

    const setDate = (newDate: Date) => {
        setMomentDate(moment(newDate));
        if (!props.shouldShowDone) {
            props.onChange(newDate);
        }
    };

    let picker = (
        <div className={classNames(
            Styles["date-picker"],
            Styles["date-single-picker"],
            !props.shouldMenu ? props.calendarClassName : undefined,
        )}
        >
            <div className={classNames(
                Styles["date-picker-container"],
            )}
            >
                <DayPickerSingleDateController
                    date={date}
                    onDateChange={selected => {
                        if (selected) {
                            const copy = date?.toDate() ?? new Date();
                            const dateValue = selected.toDate();
                            copy?.setMonth(dateValue.getUTCMonth());
                            copy?.setDate(dateValue.getUTCDate());
                            copy?.setFullYear(dateValue.getUTCFullYear());
                            setDate(copy);
                        }
                    }}
                    onFocusChange={item => {
                        setFocusedInput(item.focused);
                    }}
                    initialVisibleMonth={null}
                    focused={focusedInput}
                    numberOfMonths={1}
                    enableOutsideDays
                    renderNavNextButton={navProps => (
                        <Button
                            variant="ghost-gray"
                            prefixIcon={<RiArrowRightSLine />}
                            className={classNames(
                                "DayPickerNavigation_button__horizontalDefault",
                                "DayPickerNavigation_rightButton__horizontalDefault",
                            )}
                            {...navProps}
                        />
                    )}
                    renderNavPrevButton={navProps => (
                        <Button
                            variant="ghost-gray"
                            prefixIcon={<RiArrowLeftSLine />}
                            className={classNames(
                                "DayPickerNavigation_button__horizontalDefault",
                                "DayPickerNavigation_leftButton__horizontalDefault",
                            )}
                            {...navProps}
                        />
                    )}
                    hideKeyboardShortcutsPanel
                />
                {props.allowTime && (
                    <div className={Styles["time-picker"]}>
                        <div>
                            <h5>
                                Time
                            </h5>
                            {props.allowNewTime ? (
                                <TimePicker
                                    isRequired
                                    className={Styles["time-picker-input"]}
                                    onChange={time => {
                                        const copy = date?.toDate() ?? new Date();
                                        copy.setHours(time.hours);
                                        copy.setMinutes(time.minutes);
                                        setDate(copy);
                                    }}
                                    value={date ? TimeDate.fromDate(date.toDate()) : undefined}
                                />
                            ) : (
                                <input
                                    type="time"
                                    required
                                    value={date ? toInputHourMinute(date.toDate()) : ""}
                                    onChange={evt => {
                                        const timeValue = evt.target.valueAsDate;
                                        if (timeValue) {
                                            const copy = date?.toDate() ?? new Date();
                                            copy?.setHours(timeValue.getUTCHours());
                                            copy?.setMinutes(timeValue.getUTCMinutes());
                                            copy?.setSeconds(timeValue.getUTCSeconds());
                                            setDate(copy);
                                        }
                                    }}
                                    className="form-control"
                                />
                            )}
                        </div>
                        {props.shouldShowDone && (
                            <Button
                                size="small"
                                variant="outline-blue"
                                onClick={() => {
                                    props.onChange(date?.toDate());
                                }}
                            >
                                Done
                            </Button>
                        )}

                    </div>
                )}

            </div>
        </div>
    );
    if (props.shouldMenu) {
        picker = (
            <Dropdown.Menu className={props.calendarClassName}>
                {picker}
            </Dropdown.Menu>
        );
    }

    if (props.shouldPortal) {
        picker = createPortal(picker, document.body);
    }
    return picker;
}

DateCalendarPicker.defaultProps = {

    className: "text-light-400 p-0 me-3",
    icon: undefined,
    toggleText: undefined,
    shouldMenu: false,
    shouldPortal: false,
    allowTime: false,
    allowNewTime: false,
    calendarClassName: undefined,
    shouldShowDone: true,
};
